import {
  amountInWords, escapeHtml, formatAmount, formatDate, formatRate,
} from './invoice-format';
import { PartySnapshot, SellerSnapshot } from './invoice-snapshot';

export interface InvoiceLine {
  title: string;
  hsnCode: string | null;
  quantity: number;
  unitPrice: number;
  taxableValue: number;
  gstRateBps: number;
  gstAmount: number;
  lineTotal: number;
}

export interface InvoiceDocument {
  invoiceNumber: string;
  issuedAt: Date;
  kind: 'tax_invoice' | 'credit_note';
  seller: SellerSnapshot;
  buyer: PartySnapshot;
  placeOfSupply: string | null;
  isInterState: boolean;
  lines: InvoiceLine[];
  taxableAmount: number;
  cgstTotal: number;
  sgstTotal: number;
  igstTotal: number;
  tcsTotal: number;
  shippingTotal: number;
  discountTotal: number;
  grandTotal: number;
}

const e = escapeHtml;

/** An address block, skipping the parts that are not filled in. */
function partyLines(party: PartySnapshot): string {
  const cityLine = [party.city, party.state].filter(Boolean).join(', ');
  const withPin = [cityLine, party.postalCode].filter(Boolean).join(' - ');

  const lines = [...party.addressLines, withPin].filter((line) => line && line.trim());

  // An empty panel reads as a rendering fault. Saying the address is missing is
  // both true and actionable — the merchant can go and collect it.
  if (!lines.length) return '<div class="missing">No address on this order</div>';

  return lines.map((line) => `<div>${e(line)}</div>`).join('');
}

/**
 * The invoice, as HTML.
 *
 * A string template rather than a view engine on purpose: this is the only
 * HTML the API produces, it must render identically for ever, and a template
 * engine's helpers and partials are exactly the kind of shared surface that
 * changes underneath a document that is not allowed to change.
 *
 * Everything is inline — no webfont, no logo URL, no stylesheet. A network
 * fetch inside the render path is a dependency that fails at the worst moment,
 * and `page.setContent` with `waitUntil: 'load'` would not wait for it anyway.
 */
export function renderInvoiceHtml(doc: InvoiceDocument): string {
  const title = doc.kind === 'credit_note' ? 'CREDIT NOTE' : 'TAX INVOICE';
  const gstLabel = doc.isInterState ? 'IGST' : 'CGST + SGST';

  const rows = doc.lines
    .map(
      (line, index) => `
      <tr>
        <td class="num">${index + 1}</td>
        <td>${e(line.title)}</td>
        <td class="mono">${e(line.hsnCode ?? '—')}</td>
        <td class="num">${line.quantity}</td>
        <td class="num mono">${formatAmount(line.unitPrice)}</td>
        <td class="num mono">${formatAmount(line.taxableValue)}</td>
        <td class="num mono">${formatRate(line.gstRateBps)}%</td>
        <td class="num mono">${formatAmount(line.gstAmount)}</td>
        <td class="num mono strong">${formatAmount(line.lineTotal)}</td>
      </tr>`
    )
    .join('');

  // Rows that are zero are omitted rather than printed as 0.00 — a line saying
  // "Shipping 0.00" invites a question that has no answer.
  const extraTotals = [
    doc.discountTotal ? row('Discount', `- ${formatAmount(doc.discountTotal)}`) : '',
    doc.shippingTotal ? row('Shipping', formatAmount(doc.shippingTotal)) : '',
    doc.tcsTotal ? row('TCS', formatAmount(doc.tcsTotal)) : '',
  ].join('');

  const taxRows = doc.isInterState
    ? row(`Total IGST (${dominantRate(doc)})`, formatAmount(doc.igstTotal))
    : row(`Total CGST (${dominantRate(doc, 2)})`, formatAmount(doc.cgstTotal)) +
      row(`Total SGST (${dominantRate(doc, 2)})`, formatAmount(doc.sgstTotal));

  const bank = doc.seller.bank;
  const bankBlock = bank
    ? `
      <div class="panel bank">
        <div class="panel-title">Bank Details</div>
        ${bankRow('Bank Name', bank.bankName)}
        ${bankRow('Account No', bank.accountNumber)}
        ${bankRow('IFSC Code', bank.ifsc)}
      </div>`
    : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${e(doc.invoiceNumber)}</title>
<style>
  /* System fonts only. A webfont here is a network call in the render path. */
  * { box-sizing: border-box; }
  body {
    color: #1f2937;
    font: 11px/1.45 "Helvetica Neue", Helvetica, Arial, sans-serif;
    margin: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .mono { font-family: "SF Mono", "DejaVu Sans Mono", Menlo, Consolas, monospace; }
  .num { text-align: right; white-space: nowrap; }
  .strong { font-weight: 700; }
  .muted { color: #6b7280; }
  .missing { color: #9ca3af; font-style: italic; }

  /* ── Header ─────────────────────────────────────────────── */
  header { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; }
  .brand { display: flex; gap: 12px; align-items: flex-start; }
  .brand img { height: 40px; width: auto; }
  .seller-name { font-size: 17px; font-weight: 700; color: #111827; margin-bottom: 3px; }
  .seller-address { color: #6b7280; font-size: 10.5px; }
  .seller-gstin { margin-top: 5px; font-size: 10.5px; }
  .doc-title { font-size: 22px; font-weight: 700; letter-spacing: 0.02em; text-align: right; }
  .doc-meta { margin-top: 10px; font-size: 10.5px; }
  .doc-meta div { display: flex; gap: 10px; justify-content: flex-end; margin-top: 3px; }
  .doc-meta .label { color: #6b7280; }

  /* ── Party panels ───────────────────────────────────────── */
  .panels { display: flex; gap: 14px; margin-top: 22px; }
  .panel { flex: 1; background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 4px; padding: 11px 13px; }
  .panel-title { color: #6b7280; font-size: 8.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  .party-name { font-size: 13px; font-weight: 700; color: #111827; margin: 7px 0 3px; }
  .panel-sep { border-top: 1px solid #e5e7eb; margin: 8px 0; }
  .supply-value { font-size: 13px; font-weight: 700; color: #111827; margin-top: 3px; }

  /* ── Items ──────────────────────────────────────────────── */
  table.items { width: 100%; border-collapse: collapse; margin-top: 24px; }
  table.items thead th {
    border-bottom: 1.5px solid #d1d5db;
    color: #6b7280;
    font-size: 8.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    padding: 0 6px 6px;
    text-transform: uppercase;
    white-space: nowrap;
  }
  table.items thead th.num { text-align: right; }
  table.items tbody td { padding: 7px 6px; vertical-align: top; }
  /* Zebra striping survives to paper only with print-color-adjust, set above. */
  table.items tbody tr:nth-child(even) { background: #f8fafc; }

  /* A table that breaks across pages must repeat its header, and no single row
     may be split down the middle. */
  thead { display: table-header-group; }
  tr { break-inside: avoid; }

  /* ── Footer blocks ──────────────────────────────────────── */
  .foot { display: flex; gap: 20px; margin-top: 22px; align-items: flex-start; }
  .foot-left { flex: 1.15; }
  .words-title { color: #6b7280; font-size: 8.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  .words { font-weight: 700; margin-top: 5px; }
  .bank { margin-top: 14px; background: #fff; }
  .bank-row { display: flex; gap: 10px; margin-top: 5px; font-size: 10.5px; }
  .bank-row .label { color: #6b7280; width: 74px; }
  .totals { flex: 0 0 40%; background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 4px; padding: 13px 15px; }
  .total-row { display: flex; justify-content: space-between; gap: 14px; margin-bottom: 8px; font-size: 10.5px; }
  .total-row .label { color: #6b7280; text-align: right; }
  .grand { border-top: 1.5px solid #d1d5db; margin-top: 12px; padding-top: 11px; display: flex; justify-content: space-between; align-items: flex-end; }
  .grand-label { font-size: 13px; font-weight: 700; }
  .grand-value { font-size: 19px; font-weight: 700; text-align: right; }
  .grand-currency { font-size: 11px; font-weight: 400; color: #6b7280; }

  .rule { border-top: 1px solid #e5e7eb; margin-top: 26px; padding-top: 9px; }
  .note { color: #9ca3af; font-size: 9px; text-align: center; }
</style>
</head>
<body>
  <header>
    <div class="brand">
      ${doc.seller.logoUrl ? `<img src="${e(doc.seller.logoUrl)}" alt="" />` : ''}
      <div>
        <div class="seller-name">${e(doc.seller.name)}</div>
        <div class="seller-address">${partyLines(doc.seller)}</div>
        ${doc.seller.gstin ? `<div class="seller-gstin"><span class="muted">GSTIN:</span> <span class="mono">${e(doc.seller.gstin)}</span></div>` : ''}
      </div>
    </div>

    <div>
      <div class="doc-title">${title}</div>
      <div class="doc-meta">
        <div><span class="label">Invoice No:</span> <span class="mono strong">${e(doc.invoiceNumber)}</span></div>
        <div><span class="label">Date:</span> <span class="mono">${formatDate(doc.issuedAt)}</span></div>
      </div>
    </div>
  </header>

  <div class="panels">
    <div class="panel">
      <div class="panel-title">Billed To</div>
      <div class="party-name">${e(doc.buyer.name)}</div>
      <div class="muted">${partyLines(doc.buyer)}</div>
      ${doc.buyer.gstin ? `<div class="panel-sep"></div><div><span class="muted">GSTIN:</span> <span class="mono">${e(doc.buyer.gstin)}</span></div>` : ''}
    </div>

    <div class="panel">
      <div class="panel-title">Supply Details</div>
      <div class="muted" style="margin-top:7px">Place of Supply:</div>
      <div class="supply-value">
        ${e(doc.placeOfSupply ?? '—')} (${doc.isInterState ? 'Inter-state' : 'Intra-state'})
      </div>
      <div class="panel-sep"></div>
      <div class="muted">Tax charged as ${gstLabel}</div>
    </div>
  </div>

  <table class="items">
    <thead>
      <tr>
        <th class="num">#</th>
        <th style="text-align:left">Description</th>
        <th style="text-align:left">HSN Code</th>
        <th class="num">Qty</th>
        <th class="num">Rate</th>
        <th class="num">Taxable Value</th>
        <th class="num">GST %</th>
        <th class="num">GST Amount</th>
        <th class="num">Total</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="foot">
    <div class="foot-left">
      <div class="words-title">Amount in Words</div>
      <div class="words">${e(amountInWords(doc.grandTotal))}</div>
      ${bankBlock}
    </div>

    <div class="totals">
      ${row('Total Taxable Value', formatAmount(doc.taxableAmount))}
      ${taxRows}
      ${extraTotals}
      <div class="grand">
        <div class="grand-label">Grand Total</div>
        <div>
          <div class="grand-currency">&#8377;</div>
          <div class="grand-value">${formatAmount(doc.grandTotal)}</div>
        </div>
      </div>
    </div>
  </div>

  <div class="rule">
    <div class="note">
      ${doc.seller.gstin ? 'This is a computer-generated tax invoice and does not require a signature.' : 'This is a computer-generated invoice and does not require a signature.'}
    </div>
  </div>
</body>
</html>`;
}

function row(label: string, value: string): string {
  return `<div class="total-row"><div class="label">${escapeHtml(label)}</div><div class="mono">${value}</div></div>`;
}

function bankRow(label: string, value: string | null): string {
  if (!value) return '';
  return `<div class="bank-row"><div class="label">${escapeHtml(label)}</div><div class="mono">${escapeHtml(value)}</div></div>`;
}

/**
 * The GST rate to print beside a total.
 *
 * Almost every order is a single rate, and naming it ("Total IGST (18%)") is
 * what the reference invoice does. A mixed-rate order genuinely has no single
 * rate to name, so it says so rather than picking one and being wrong.
 */
function dominantRate(doc: InvoiceDocument, divisor = 1): string {
  const rates = new Set(doc.lines.map((line) => line.gstRateBps));
  if (rates.size !== 1) return 'mixed';

  const [only] = [...rates];
  return `${formatRate(only / divisor)}%`;
}
